import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import {
  ApplicationRef,
  Inject,
  Injectable,
  PLATFORM_ID,
  RendererFactory2,
} from '@angular/core';
import { SwUpdate, UpdateAvailableEvent } from '@angular/service-worker';
import { PluginRegistry } from '@capacitor/core';
import { ABTEST_WHITELIST_COOKIE } from '@freelancer/abtest';
import { TimeUtils } from '@freelancer/time-utils';
import { CookieService } from '@laurentgoudet/ngx-cookie';
import * as Rx from 'rxjs';
import { first } from 'rxjs/operators';

/**
 * Custom AppData from the Ngsw config included in the update notifications.
 */
export interface CustomAppData {
  /* Git SHA of the version */
  gitRevision: string;
  /* Build time as a Unix timestamp of the version */
  buildTimestamp: number;
  /* Minimum version build time to force the upgrade to */
  minimumVersionTimestamp: number;
}

/**
 * Wraps Angular's SwUpdate service & provide additional PWA-related helpers
 */
@Injectable({
  providedIn: 'root',
})
export class Pwa {
  private backgroundUpdateChecksSubscription?: Rx.Subscription;
  private isInstalledModeActivated = false;

  constructor(
    private appRef: ApplicationRef,
    private cookies: CookieService,
    private rendererFactory: RendererFactory2,
    private updates: SwUpdate,
    private timeUtils: TimeUtils,
    @Inject(ABTEST_WHITELIST_COOKIE) private whitelistCookie: string,
    @Inject(DOCUMENT) private document: HTMLDocument,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) {}

  /**
   * Detects if the PWA is running in installed mode, i.e. launched as a
   * standalone app.
   *
   * Use that to hide "Install app" banners & buttons for instance.
   */
  isInstalled(): boolean {
    return (
      isPlatformBrowser(this.platformId) &&
      ((window.navigator && (window.navigator as any).standalone === true) ||
        this.isInstalledModeActivated)
    );
  }

  /**
   * Promise of the Capacitor Plugins object allowing the PWA to use native
   * APIs
   *
   * /!\ Only available in installed mode, always wrap the calls if
   * `this.pwa.isInstalled()` blocks.
   */
  capacitorPlugins(): Promise<PluginRegistry> {
    return import('@capacitor/core').then(c => c.Plugins);
  }

  /**
   * @private
   * Activate the PWA installed mode, i.e. tweak a bit the global styles &
   * behaviors
   *
   * This is turned on when the PWA is running in installed mode, or when it is
   * loaded from the `/internal/pwa` route for easier testing.
   */
  activateInstalledMode(): void {
    if (!this.isInstalledModeActivated) {
      // Disable text selection & browser callouts as it's not "app-like"
      const renderer = this.rendererFactory.createRenderer(null, null);
      renderer.setStyle(this.document.body, '-webkit-user-select', 'none');

      if (isPlatformBrowser(this.platformId)) {
        // Enroll all PWA users into whitelist for now to get the most webapp
        // features
        this.cookies.put(this.whitelistCookie, 'true');

        // Turn off the m. redirections
        this.cookies.put('mobile_optout', 'true');

        // Load Capacitor (PWA <-> native bridge)
        // FIXME: https://github.com/ionic-team/capacitor/issues/456
        // We have to use a dynamic import here as otherwise it breaks IE11
        import('@capacitor/core');
      }

      this.isInstalledModeActivated = true;
    }
  }

  /**
   * @private
   * Use these events to notify the user of a pending update or to refresh
   * their pages when the code they are running is out of date.
   */
  hasPendingUpdate(): Rx.Observable<UpdateAvailableEvent> {
    return this.updates.available;
  }

  /**
   * @private
   * Check for available updates
   */
  checkForUpdate(): Promise<void> {
    return this.updates.checkForUpdate();
  }

  /**
   * @private
   * Update to the latest app version immediately
   */
  activateUpdate(): Promise<void> {
    return this.updates
      .activateUpdate()
      .then(() => this.document.location.reload());
  }

  /**
   * @private
   * Ask the service worker to check if any updates have been deployed to the
   * server every 6 hours
   */
  startBackgroundUpdateChecks() {
    if (this.updates.isEnabled) {
      const appIsStable$ = this.appRef.isStable.pipe(
        first(isStable => isStable),
      );
      const everySixHours$ = this.timeUtils.rxInterval(6 * 60 * 60 * 1000);
      const everySixHoursOnceAppIsStable$ = Rx.concat(
        appIsStable$,
        everySixHours$,
      );
      this.backgroundUpdateChecksSubscription = everySixHoursOnceAppIsStable$.subscribe(
        () => this.updates.checkForUpdate(),
      );
    }
  }

  /**
   * @private
   * Cancel the background update checks
   */
  stopBackgroundUpdateChecks() {
    if (this.backgroundUpdateChecksSubscription) {
      this.backgroundUpdateChecksSubscription.unsubscribe();
    }
  }
}
